import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute component that guards routes requiring authentication
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authenticated
 * @param {boolean} props.requireAdmin - If true, requires admin or subadmin role
 * @param {string[]} props.allowedRoles - Array of allowed admin roles (e.g., ['admin', 'subadmin'])
 */
const ProtectedRoute = ({ 
  children, 
  requireAdmin = false, 
  allowedRoles = ['admin', 'subadmin'] 
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to home page with state to show login modal
    return <Navigate to="/" state={{ from: location, showLogin: true }} replace />;
  }

  // Check if admin access is required
  if (requireAdmin) {
    // Get role from context user
    let userRole = user?.adminRole || 'user';
    
    // Fallback: Also check localStorage to prevent race condition issues
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // If localStorage has admin role but context doesn't, use localStorage
        if ((parsedUser.adminRole === 'admin' || parsedUser.adminRole === 'subadmin') && userRole === 'user') {
          userRole = parsedUser.adminRole;
        }
      } catch (e) {
        console.error('Error parsing stored user in ProtectedRoute:', e);
      }
    }
    
    const hasAccess = allowedRoles.includes(userRole);
    
    if (!hasAccess) {
      // User is logged in but doesn't have admin access
      // Redirect to home page or show unauthorized message
      return <Navigate to="/" state={{ unauthorized: true }} replace />;
    }
  }

  // User is authenticated and has required access
  return children;
};

export default ProtectedRoute;
